/**
 * Does a one level deep check for equality on object properties
 * @param  {Object}  objA An object to compare
 * @param  {Object}  objB The other object to compare
 * @return {Boolean}      returns true if each object property value match
 */
export function isObjEqual( objA, objB ) {
    const a = Object.getOwnPropertyNames( objA );
    const b = Object.getOwnPropertyNames( objB );

    return a.length !== b.length
           ? false
           : a.every( prop => objA[ prop ] === objB[ prop ] );
}


/**
 * Validates item(s) are non-empty strings
 * @param  {String|Array} strings String or array of strings to test
 * @return {Boolean}              True if all items are strings and none are empty
 */
export function validateStrings( strings ) {
    if ( typeof strings === 'string' ) {
        return String( strings ).trim().length > 0;
    } else if ( Array.isArray( strings ) ) {
        return strings.every( str => typeof str === 'string' && String( str ).trim().length > 0 );
    } else {
        return false;
    }
}

export function formatDateAsMonthDay( date, locale = 'en-US' ) {
    date = new Date( date );
    const month = new Intl.DateTimeFormat( locale, { month: 'short' } ).format( date );
    const day = new Intl.DateTimeFormat( locale, { day: 'numeric' } ).format( date );
    return `${ month } ${ day }`;
}

export function formatDateAsMonthYear( date, locale = 'en-US' ) {
    date = new Date( date );
    const year = new Intl.DateTimeFormat( locale, { year: 'numeric' } ).format( date );
    const month = new Intl.DateTimeFormat( locale, { month: 'long' } ).format( date );
    return `${ month } ${ year }`;
}

export function formatDateAsYearMonthNumber( date ) {
    date = new Date( date );
    const year = date.getFullYear();
    let month = date.getMonth() + 1;
    month = month.length < 2 ? `0${ month }` : month;

    return `${ year }${ month }`;
}

/**
 * Formats a date object or valid date string based on locale and options
 * @param  {Date|string} date - Date object or date string to format
 * @param  {string} locale    - locale string
 * @param  {Object} [options] - optional object for formatting that will be
 *                              passed to Intl.DateTimeFormat()
 * @return {string}           - formatted date returned as a string
 */
export function formatDate( date, locale, options ) {
    date = date instanceof Date ? date : new Date( date.toString().replace( /([+-]\d\d)(\d\d)$/, '$1:$2' ) );

    // If no options are passed, use these defaults
    options = options ? options : {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    };

    return new Intl.DateTimeFormat( locale, options ).format( date );
}

/**
 * Calculates hours/days/minutes between two dates
 * @param  {Object} futureDate Date object representing the future when the
 *                             countdown will end
 * @param  {Object} date       An optional date object. Defaults to current date.
 * @return {Object}            Object containing days, hours, minutes
 */
export function calculateCountdown( futureDate, date = new Date() ) {
    let now = date.getTime();
    let future = futureDate.getTime();
    let diff = ( ( future - now ) / 1000 ) / 60;
    let minutes, hours, days;

    minutes = ~~( diff % 60 );
    diff = diff / 60;

    hours = ~~( diff % 24 );
    days = ~~( diff / 24 );

    return { days, hours, minutes };
}

/**
 * Determines whether or not the site is running on Android
*/
export function isAndroid() {
    return /Android/i.test( navigator.userAgent );
}

/**
 * Determines whether or not the site is running on iOS
*/
export function isIOS() {
    return /iPhone|iPad|iPod/i.test( navigator.userAgent );
}

/**
 * Smooth scroll to top of page with simple easing
 */
export function scrollToTop() {
    // document.documentElement.scrollTop is 0 on mobile, falling back to body.scrollTop in that case.
    const scrollPosition = document.documentElement.scrollTop || document.body.scrollTop;
    const amount = 20;
    const eased = ~~( scrollPosition / amount );

    if ( scrollPosition ) {
        // This contains a bitwise implementation of Math.max() which is only used here
        // because it's significantly faster in IE 11 and Safari
        window.scrollBy( 0, -( amount ^ ( ( amount ^ eased ) & -( amount < eased ) ) ) );
        requestAnimationFrame( scrollToTop );
    }
}

/**
 * Gets red, green, and blue values from a valid hex color string, e.g. #ff00ff
 * @param  {string} hex     A valid hex color string starting with a # sign
 * @return {object|boolean} Object containing r, g, b values or false if hex is invalid
 */
export function getRgb( hex ) {
    if ( hex && hex.charAt( 0 ) === '#' && hex.length === 7 ) {
        const color = `0x${ hex.substr( 1 ) }`;

        if ( color > 0xffffff || color < 0 ) {
            return false; // not a valid color range
        }

        const r = color >> 16;
        const g = color >> 8 & 0xff;
        const b = color & 0xff;

        return { r, g, b };
    }

    return false;
}

/**
 * useEffect function to trigger when user resizes window
 * example: useEffect(onResizeEffect(() => { do something }), []);
 * @param {function} resizeCb callback function to call on init and every legitimate resize event
 * @returns function for useEffect
 */
export function onResizeEffect( resizeCb ) {


    return () => {
        let width = window.innerWidth;
        let timeout = false;

        const fn = () => {
            // see if a horizontal resize actually happened. necessary for mobile becasue scrolling triggers this
            const newWidth = window.innerWidth;
            if ( newWidth == width ) {
                return;
            }

            width = newWidth;
            clearTimeout( timeout );
            timeout = setTimeout( resizeCb, 100 );
        };
        
        resizeCb();
        window.addEventListener( 'resize', fn );

        return () => {
            window.removeEventListener( 'resize', fn );
        };
    };

}

export function localizeNumber( num, locale ) {
    return num.toLocaleString( locale, { useGrouping: true } );
}